import React, { useState, useEffect } from 'react';
import { prohibitedDevicesApi, formatApiErrorDetail } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Ban, Plus, MoreHorizontal, Edit2, Trash2, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function ProhibitedDevicesPage() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    reason: '',
  });

  const fetchDevices = async () => {
    try {
      const { data } = await prohibitedDevicesApi.list();
      setDevices(data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDevices(); }, []);

  const resetForm = () => {
    setFormData({ brand: '', model: '', reason: '' });
    setEditingDevice(null);
  };

  const handleOpenCreate = () => { resetForm(); setShowDialog(true); };

  const handleOpenEdit = (device) => {
    setEditingDevice(device);
    setFormData({ brand: device.brand, model: device.model || '', reason: device.reason });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        brand: formData.brand.trim(),
        model: formData.model.trim() || null,
        reason: formData.reason.trim(),
      };
      if (editingDevice) {
        await prohibitedDevicesApi.update(editingDevice.id, payload);
        toast.success('Prohibited device updated');
      } else {
        await prohibitedDevicesApi.create(payload);
        toast.success('Prohibited device added');
      }
      setShowDialog(false);
      resetForm();
      fetchDevices();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (device) => {
    if (!window.confirm(`Remove ${device.brand}${device.model ? ' ' + device.model : ''} from prohibited list?`)) return;
    try {
      await prohibitedDevicesApi.delete(device.id);
      toast.success('Prohibited device removed');
      fetchDevices();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to delete');
    }
  };

  const handleToggleActive = async (device) => {
    try {
      await prohibitedDevicesApi.update(device.id, { is_active: !device.is_active });
      toast.success(device.is_active ? 'Device restriction disabled' : 'Device restriction enabled');
      fetchDevices();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filtered = devices.filter((d) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return d.brand.toLowerCase().includes(q) || (d.model || '').toLowerCase().includes(q) || d.reason.toLowerCase().includes(q);
  });

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Prohibited Devices</h1>
            <p className="text-slate-600 mt-1">Manage devices that cannot have warranty attached</p>
          </div>
          <Button onClick={handleOpenCreate} className="bg-[#005DE8] hover:bg-[#0047B3]" data-testid="add-prohibited-device-btn">
            <Plus className="h-4 w-4 mr-2" />Add Device
          </Button>
        </div>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search brand or model..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 h-11" data-testid="search-prohibited" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" /></div>
            ) : filtered.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead>Brand</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((device) => (
                    <TableRow key={device.id} className="table-row-hover">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                            <Ban className="h-5 w-5 text-red-500" />
                          </div>
                          <span className="font-medium text-slate-900">{device.brand}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{device.model || <span className="italic text-slate-400">All models</span>}</TableCell>
                      <TableCell className="text-slate-600 max-w-xs truncate">{device.reason}</TableCell>
                      <TableCell>
                        <Badge className={device.is_active ? 'bg-red-50 text-red-700 border-red-200 border' : 'bg-slate-100 text-slate-600 border-slate-200 border'}>
                          {device.is_active ? 'Active' : 'Disabled'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">{formatDate(device.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`prohibited-actions-${device.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenEdit(device)}><Edit2 className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleActive(device)}>{device.is_active ? 'Disable' : 'Enable'}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(device)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-16">
                <Ban className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No prohibited devices</h3>
                <p className="text-slate-500 mt-1">Add devices that should not have warranty attached</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>{editingDevice ? 'Edit Prohibited Device' : 'Add Prohibited Device'}</DialogTitle>
              <DialogDescription>{editingDevice ? 'Update device restriction details' : 'Block a device brand/model from having warranty attached'}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="brand">Brand *</Label>
                  <Input id="brand" value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} placeholder="e.g., Samsung" required data-testid="prohibited-brand-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model">Model (leave empty to block entire brand)</Label>
                  <Input id="model" value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} placeholder="e.g., Galaxy A05" data-testid="prohibited-model-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reason">Reason *</Label>
                  <Textarea id="reason" value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder="Why is this device prohibited..." rows={3} required data-testid="prohibited-reason-input" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#005DE8] hover:bg-[#0047B3]" disabled={saving} data-testid="prohibited-submit-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {editingDevice ? 'Update' : 'Add Device'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
